import { UNIT_TYPE_LABEL } from "./constants";

/** Loose number parse for form inputs: "12,500.50" / "12500.5" -> 12500.5, junk -> 0. */
export const n = (v) => {
  const x = Number(String(v == null ? "" : v).replace(/[^0-9.]/g, ""));
  return Number.isNaN(x) ? 0 : x;
};

export const r2 = (x) => Math.round(x * 100) / 100;

/** Formats a typed amount for display: "20000.5" -> "20,000.5". Keeps a trailing "." and at most 2 decimals. */
export const formatAmount = (v) => {
  const s = String(v == null ? "" : v).replace(/[^0-9.]/g, "");
  if (!s) return "";
  const dot = s.indexOf(".");
  const int = (dot === -1 ? s : s.slice(0, dot)).replace(/^0+(?=\d)/, "");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dot === -1 ? grouped : `${grouped || "0"}.${s.slice(dot + 1).replace(/\./g, "").slice(0, 2)}`;
};

export const money = (x) =>
  "€" + r2(x).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const ronMoney = (x, rate) =>
  "RON " + r2(x * rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtDate = (iso) =>
  iso
    ? new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

/** "01.10.2026" (extractor format) -> "2026-10-01"; anything else -> "". */
export const ddmmyyyyToIso = (value) => {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec((value || "").trim());
  return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : "";
};

/**
 * Dated payments first, in date order; undated ones keep their relative
 * order at the end (so they can be dragged). The extras row sorts by
 * `extrasDate`.
 */
export const sortByDate = (pays, extrasDate) =>
  pays
    .map((p, i) => ({ p, i, d: p.id === "extras" ? extrasDate : p.date }))
    .sort((a, b) => {
      if (a.d && b.d) return a.d < b.d ? -1 : a.d > b.d ? 1 : a.i - b.i;
      if (a.d) return -1;
      if (b.d) return 1;
      return a.i - b.i;
    })
    .map((x) => x.p);

// ---- units ------------------------------------------------------------------
// The picker lists units from GET /units/?block=&all=true (UnitSerializer),
// while units pre-filled from an extracted document come from
// GET /units/<id>/ (UnitDetailSerializer), which is shaped differently.
// Both are normalised to one shape for the wizard.

const unitLabel = (type, nr) => {
  if (type === "APT") return `Ap. ${nr}`;
  return `${UNIT_TYPE_LABEL[type] || type} ${nr}`;
};

const unitMeta = (u) => {
  const parts = [];
  if (u.floor !== null && u.floor !== undefined && u.floor !== "") parts.push(`Floor ${u.floor}`);
  if (u.num_rooms) parts.push(`${u.num_rooms} rooms`);
  if (u.net_sqm) parts.push(`${u.net_sqm} m²`);
  if (u.apt_type) parts.push(`Type ${u.apt_type}`);
  return parts.join(" · ");
};

export const unitFromList = (u) => ({
  id: u.id,
  nr: String(u.nr ?? ""),
  type: u.unit_type,
  label: unitLabel(u.unit_type, u.nr),
  meta: unitMeta(u),
  price: Number(u.initial_price) || 0,
  sold: !!u.settlement_price,
  path: [u.block?.project_name, u.block?.phase_name, u.block?.name && `Block ${u.block.name}`]
    .filter(Boolean)
    .join(" · "),
  search: [u.nr, u.apt_type, u.floor !== null ? `floor ${u.floor}` : "", u.num_rooms ? `${u.num_rooms} rooms` : ""]
    .join(" ")
    .toLowerCase(),
});

export const unitFromDetail = (d) => ({
  id: d.unit.id,
  nr: String(d.unit.nr ?? ""),
  type: d.unit.unit_type,
  label: unitLabel(d.unit.unit_type, d.unit.nr),
  meta: unitMeta(d.unit),
  price: Number(d.unit.initial_price) || 0,
  sold: !!d.contract,
  path: [d.project?.name, d.block?.name && `Block ${d.block.name}`].filter(Boolean).join(" · "),
  search: "",
});

// ---- totals & validation ----------------------------------------------------

export const computeTotals = (state) => {
  const settle = r2(state.sel.reduce((a, s) => a + n(s.settle), 0));
  const extras = r2(state.sel.reduce((a, s) => a + n(s.extra), 0));
  const total = r2(settle + extras);
  const real = state.pays.filter((p) => p.id !== "extras");
  const scheduled = r2(real.reduce((a, p) => a + n(p.amount), 0) + extras);
  const balance = r2(total - scheduled);

  const missing = [];
  if (!String(state.d.pvc).trim()) missing.push("PVC number");
  if (!state.d.date) missing.push("PVC date");
  if (!state.d.client) missing.push("client");
  if (!state.d.type) missing.push("contract type");
  let v1 = missing.length ? "Add " + missing.join(", ") : "";
  if (!v1 && state.pvcStatus === "unavailable") v1 = `PVC ${state.d.pvc} is already used this year`;

  let v2 = "";
  if (!state.sel.length) v2 = "Select at least one unit";
  else if (state.sel.some((s) => s.unit.sold)) {
    const sold = state.sel.filter((s) => s.unit.sold).map((s) => s.unit.label);
    v2 = `${sold.join(", ")} ${sold.length === 1 ? "is" : "are"} already sold — remove ${sold.length === 1 ? "it" : "them"} to continue`;
  } else if (state.sel.some((s) => n(s.settle) <= 0)) v2 = "Every unit needs a settlement price";

  let v3 = "";
  if (real.some((p) => n(p.amount) <= 0)) v3 = "Every payment needs an amount";
  else if (real.some((p) => !p.date) || (extras > 0 && !state.extrasDate))
    v3 = "Every payment except the balance needs a due date";
  else if (balance < 0) v3 = "Payments exceed the contract total by " + money(-balance);

  return { settle, extras, total, scheduled, balance, valid: ["", v1, v2, v3] };
};

// ---- payload ----------------------------------------------------------------

const toDateTime = (iso) => (iso ? `${iso}T00:00:00` : null);
const vatId = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));

/**
 * POST /api/contracts/ body (ContractCreateSerializer).
 *
 * The backend has no per-unit "extra" field: like the V1 wizard, a unit's
 * extras are folded into its settled_price and invoiced as one EXTRA
 * payment. The backend requires payments to add up to exactly the sum of
 * settled prices, which holds because the BALANCE row takes the remainder.
 */
export const buildContractPayload = (state, totals) => {
  const ordered = sortByDate(state.pays, state.extrasDate).filter((p) => p.id !== "extras" || totals.extras > 0);

  const payments = ordered.map((p) =>
    p.id === "extras"
      ? { type: "EXTRA", amount: totals.extras, vat: vatId(state.extrasVat), booking_date: toDateTime(state.extrasDate), cashed: null, cashed_date: null }
      : { type: p.type, amount: r2(n(p.amount)), vat: vatId(p.vat), booking_date: toDateTime(p.date), cashed: null, cashed_date: null }
  );
  if (totals.balance > 0) {
    payments.push({ type: "BALANCE", amount: totals.balance, vat: vatId(state.balanceVat), booking_date: null, cashed: null, cashed_date: null });
  }

  return {
    pvc_number: parseInt(state.d.pvc, 10),
    pvc_date: state.d.date,
    currency: state.d.currency,
    notary: state.d.notary,
    notes: state.d.description,
    type: state.d.type,
    client: state.d.client,
    // settled_price is the unit's amount without extras; extra_finishing is
    // stored separately. The unit's total is settled_price + extra_finishing.
    units: state.sel.map((s) => ({ unit: s.id, settled_price: r2(n(s.settle)), extra_finishing: r2(n(s.extra)) })),
    payments,
  };
};
